import React, { useEffect, useState } from 'react';

import { Box, Button } from '@mui/material';

import { PRIVACY_SECTION_ID } from './constants';
import { classes } from './styles';

interface ApplyNowProps {
  buttonText?: string;
  fontFamily?: string;
  previewMode?: boolean;
}

const CANDIDATE_DETAILS_ID = 'candidate-details';

const getApplicationStartElement = (): HTMLElement | null =>
  document.getElementById(PRIVACY_SECTION_ID) ?? document.getElementById(CANDIDATE_DETAILS_ID);

export const ApplyNow: React.FC<ApplyNowProps> = ({
  buttonText = 'Apply Now',
  fontFamily,
  previewMode = false
}) => {
  const [scrollVisible, setScrollVisible] = useState(false);
  const isVisible = previewMode || scrollVisible;

  useEffect(() => {
    if (previewMode) return undefined;

    const handleScroll = () => {
      const applicationStart = getApplicationStartElement();

      if (!applicationStart) {
        setScrollVisible(false);
        return;
      }

      const header = document.querySelector('header.wcag-header');
      const headerHeight = header ? header.offsetHeight : 80;
      const scrollY = window.pageYOffset;
      const startRect = applicationStart.getBoundingClientRect();
      const startTopDoc = startRect.top + scrollY;
      const jobDescription = document.querySelector('.job-description');
      let hasScrolledPastJobDescriptionTop = false;

      if (jobDescription) {
        const jobRect = jobDescription.getBoundingClientRect();
        const jobTopDoc = jobRect.top + scrollY;
        hasScrolledPastJobDescriptionTop = scrollY + headerHeight + 40 >= jobTopDoc;
      }

      const hasReachedApplicationArea = scrollY + window.innerHeight - 100 >= startTopDoc;
      setScrollVisible(hasScrolledPastJobDescriptionTop && !hasReachedApplicationArea);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [previewMode]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (previewMode) return;

    const applicationStart = getApplicationStartElement();
    const header = document.querySelector('header.wcag-header');

    if (applicationStart) {
      const headerHeight = header ? header.offsetHeight : 80;
      const elementTop = applicationStart.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: Math.max(0, elementTop - headerHeight - 20),
        behavior: 'smooth'
      });
    }
  };

  return (
    <Box
      aria-label="Application action"
      component="section"
      data-tlp-preview-mode={previewMode ? 'true' : undefined}
      sx={classes.applyNow.containerSx(isVisible)}
    >
      <Box sx={classes.applyNow.centerWrapperSx()}>
        {previewMode ? (
          <Button sx={classes.applyNow.buttonSx(fontFamily)} type="button">
            {buttonText}
          </Button>
        ) : (
          <Button
            component="a"
            href="#new_application"
            onClick={handleClick}
            sx={classes.applyNow.buttonSx(fontFamily)}
          >
            {buttonText}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ApplyNow;
